// TanStack Query hooks for the gateway observability API.
//
// The dashboard renders only REAL gateway responses. When a query is
// loading, in error, or has not yet produced data, the UI renders an explicit
// non-data state (loading / connection error) for that panel — it NEVER
// fabricates synthetic metrics. There is no mock fallback path.
//
// All hooks are always enabled: in production they hit the same-origin
// `/api` proxy (see functions/api/[[path]].js); in local dev they hit the
// Vite `/api` proxy. A network/server failure surfaces as an error state.

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { fetchAggregates, fetchEvents, fetchHealth, fetchTokenSeries } from '../lib/gateway-client'
import {
  AGGREGATES_REFETCH_MS,
  EVENTS_REFETCH_MS,
  EVENTS_LIMIT,
  HEALTH_REFETCH_MS,
} from '../lib/env'
import { useShallow } from 'zustand/react/shallow'
import { selectFilters, useFilterStore } from '../store/filter-store'
import type {
  AggregatesResponse,
  EventsResponse,
  HealthSnapshot,
  TokenBucket,
  TokenSeriesResponse,
} from '../types/gateway'

export function useHealthQuery() {
  return useQuery({
    queryKey: ['gateway', 'health'],
    queryFn: fetchHealth,
    refetchInterval: HEALTH_REFETCH_MS,
    staleTime: HEALTH_REFETCH_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

export function useAggregatesQuery() {
  const filters = useFilterStore(useShallow(selectFilters))
  return useQuery({
    queryKey: ['gateway', 'aggregates', filters],
    queryFn: () => fetchAggregates(filters),
    refetchInterval: AGGREGATES_REFETCH_MS,
    staleTime: AGGREGATES_REFETCH_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

export function useEventsQuery() {
  return useQuery({
    queryKey: ['gateway', 'events', EVENTS_LIMIT],
    queryFn: () => fetchEvents(EVENTS_LIMIT),
    refetchInterval: EVENTS_REFETCH_MS,
    staleTime: EVENTS_REFETCH_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

export interface TokenSeriesQueryFilters {
  from: string
  to: string
  bucket: TokenBucket
  route?: string | null
  model?: string | null
  channel?: string | null
}

export function useTokenSeriesQuery(filters: TokenSeriesQueryFilters) {
  return useQuery<TokenSeriesResponse>({
    queryKey: ['gateway', 'tokens', filters],
    queryFn: () => fetchTokenSeries(filters),
    refetchInterval: AGGREGATES_REFETCH_MS,
    staleTime: AGGREGATES_REFETCH_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

// Aggregated view of all three queries for the operations route. Numeric
// fields are only set from real responses; any missing/error query surfaces
// as null data plus a flag so the route renders explicit non-data UI.
export interface DashboardQueries {
  health: HealthSnapshot | null
  aggregates: AggregatesResponse | null
  events: EventsResponse | null
  isLoading: boolean
  isError: boolean
}

export function useDashboardQueries(): DashboardQueries {
  const healthQ = useHealthQuery()
  const aggregatesQ = useAggregatesQuery()
  const eventsQ = useEventsQuery()

  const someLoading =
    healthQ.isLoading || aggregatesQ.isLoading || eventsQ.isLoading
  const someError = healthQ.isError || aggregatesQ.isError || eventsQ.isError

  return {
    health: healthQ.data ?? null,
    aggregates: aggregatesQ.data ?? null,
    events: eventsQ.data ?? null,
    isLoading: someLoading,
    isError: someError,
  }
}
